import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { X, FileText, Mail, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentModal({ isOpen, onClose, paymentType, token, onSuccess }) {
  const [processing, setProcessing] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(false);

  useEffect(() => {
    // Check if we're returning from Stripe
    const urlParams = new URLSearchParams(window.location.search);
    const returnedSessionId = urlParams.get('session_id');
    
    if (returnedSessionId && isOpen) {
      setSessionId(returnedSessionId);
      pollPaymentStatus(returnedSessionId);
    }
  }, [isOpen]);

  const prices = {
    pdf: { amount: 0.79, label: 'PDF Export', icon: FileText },
    email: { amount: 1.99, label: 'Email Delivery', icon: Mail }
  };

  const currentPrice = prices[paymentType];

  const handlePurchase = async () => {
    setProcessing(true);
    try {
      const hostUrl = window.location.origin;
      const response = await axios.post(
        `${API}/create-checkout-session`,
        {
          payment_type: paymentType,
          host_url: hostUrl
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start payment process');
      setProcessing(false);
    }
  };

  const pollPaymentStatus = async (checkSessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please refresh the page.');
      setPollingStatus(false);
      return;
    }

    setPollingStatus(true);

    try {
      const response = await axios.get(`${API}/checkout-status/${checkSessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'paid') {
        toast.success('Payment successful!');
        setPollingStatus(false);
        
        // Clear session_id from URL
        window.history.replaceState({}, document.title, window.location.pathname);
        
        if (onSuccess) {
          onSuccess();
        }
        onClose();
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setPollingStatus(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(checkSessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error checking payment status');
      setPollingStatus(false);
    }
  };

  if (!isOpen || !currentPrice) return null;

  const Icon = currentPrice.icon;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md border-0 shadow-2xl">
        <CardHeader className="relative">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={processing || pollingStatus}
          >
            <X className="w-5 h-5" />
          </button>
          <CardTitle className="flex items-center gap-2 text-2xl">
            <Icon className="w-6 h-6" />
            Purchase {currentPrice.label}
          </CardTitle>
          <CardDescription>
            One-time purchase for this feature
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {pollingStatus ? (
            <div className="text-center py-8 space-y-4">
              <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto" />
              <p className="text-sm text-gray-600">Verifying payment...</p>
            </div>
          ) : (
            <>
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-6 text-center">
                <p className="text-sm text-gray-600 mb-2">Total Amount</p>
                <p className="text-5xl font-bold text-blue-600">
                  ${currentPrice.amount.toFixed(2)}
                </p>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold text-sm text-gray-700">What you get:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  {paymentType === 'pdf' && (
                    <>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        Professional PDF report with full estimate details
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        Provider comparison and quality scores
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        Downloadable and printable format
                      </li>
                    </>
                  )}
                  {paymentType === 'email' && (
                    <>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        Email delivery to any address
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        Full estimate details and provider comparison
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                        Formatted and professional email
                      </li>
                    </>
                  )}
                </ul>
              </div>

              <Button
                onClick={handlePurchase}
                disabled={processing}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Pay $${currentPrice.amount.toFixed(2)}`
                )}
              </Button>

              <p className="text-xs text-center text-gray-500">
                Secure payment powered by Stripe
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
